import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import api from "../api";
import { company } from "../mock";

function readHash() {
  const raw = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  return new URLSearchParams(raw);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [linkError, setLinkError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const p = readHash();
    if (p.get("error") || p.get("error_description")) {
      setLinkError(p.get("error_description") || "This reset link is invalid or expired.");
      return;
    }
    if (p.get("type") === "recovery" && p.get("access_token")) {
      setToken(p.get("access_token"));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      setLinkError("This reset link is invalid or has expired.");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { recovery_access_token: token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "This reset link is invalid or has expired. Please request a new one.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="absolute inset-0 dot-grid opacity-60" />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60">
        <div className="mb-6 flex items-center gap-3">
          <img src={company.logo} alt={company.legal} className="h-10 w-10 rounded-lg object-cover" />
          <div className="text-sm font-bold text-slate-900">{company.name}</div>
        </div>

        {done ? (
          <div data-testid="reset-success" className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-slate-900">Password Updated Successfully</h2>
            <p className="mt-2 text-sm text-slate-500">You can now sign in with your new password.</p>
            <Button onClick={() => navigate("/")} data-testid="return-to-login-btn" className="btn-primary-gradient mt-6 w-full border-0 text-white">Return to Login</Button>
          </div>
        ) : linkError ? (
          <div data-testid="reset-link-error" className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
            <h2 className="mt-4 text-2xl font-bold text-slate-900">Link Expired or Invalid</h2>
            <p className="mt-2 text-sm text-slate-500">{linkError} Please request a new reset link.</p>
            <Link to="/forgot-password" className="mt-6 inline-block"><Button className="btn-primary-gradient w-full border-0 text-white">Request a new link</Button></Link>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-slate-900">Create New Password</h2>
            <p className="mt-1 text-sm text-slate-500">Enter and confirm your new password below.</p>
            {error && <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="np">New Password</Label>
                <Input id="np" data-testid="new-password-input" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cp">Confirm New Password</Label>
                <Input id="cp" data-testid="confirm-password-input" type="password" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
              </div>
              <Button type="submit" disabled={busy || !token} data-testid="update-password-btn" className="btn-primary-gradient w-full border-0 text-white">
                {busy ? "Updating…" : "Update Password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
